import { ReportFiltersValues } from '../features/Analytics/stores/useAnalyticsFiltersStore/types'
import {
    BenchmarkingReportSettings,
    GHGEmissionsReportSettings,
    IntervalDataTypeParam,
    IntervalReportSettings,
    TimezoneEntry,
    TotalEmissionsReportSettings,
    TotalEmissionsReportType,
    UsageCostDataSet,
} from '@/hooks'

export const SITE_TITLE_PLACEHOLDER = '__SITE_TITLE__'
export const LEGACY_URL_PLACEHOLDER = '__LEGACY_URL__'
export const CONTACT_EMAIL = 'contact@myenergynetwork.com'
export const FEEDBACK_SURVEY_URL =
    'https://us21.list-manage.com/survey?u=7fa33eedb7c173d34f5514d3e&id=32a42858e8&attribution=f%E2%80%A6'

export const DEFAULT_AUTO_ANIMATE_SETTINGS = {
    duration: 150,
    easing: 'ease-in-out',
}

export const DEFAULT_ANALYTICS_FILTERING: ReportFiltersValues = {
    commodity: [1],
    calculation: 'productionMonth',
    contract: [],
    timeline: {
        type: 'is',
        value: 'last12Months',
    },
    account: [],
    node: [],
    facility: [],
    tag: [],
    provider: [],
    meter: [],
    usn: [],
    invoiceNo: [],
    unitOfMeasureId: [2],
}

export type SustainabilityFiltersValues = Pick<
    ReportFiltersValues,
    | 'node'
    | 'commodity'
    | 'contract'
    | 'timeline'
    | 'tag'
    | 'facility'
    | 'meter'
>

export type BillingFiltersValues = Pick<
    ReportFiltersValues,
    | 'node'
    | 'commodity'
    | 'contract'
    | 'timeline'
    | 'tag'
    | 'facility'
    | 'meter'
>

export type ActiveManagementFiltersValues = Pick<
    ReportFiltersValues,
    'node' | 'tag' | 'facility' | 'usn' | 'meter' | 'timeline' | 'subMeter'
> & { timezone?: string; type?: IntervalDataTypeParam }

export type ActiveManagementTimezonesFiltersValues = Pick<
    ActiveManagementFiltersValues,
    'node' | 'tag' | 'facility'
>

export const DEFAULT_SUSTAINABILITY_FILTERING: SustainabilityFiltersValues = {
    node: [],
    commodity: [],
    contract: [],
    timeline: {
        type: 'is',
        value: 'last12Months',
    },
    tag: [],
    facility: [],
    meter: [],
}

export const DEFAULT_ACTIVE_MANAGEMENT_FILTERING: ActiveManagementFiltersValues =
    {
        node: [],
        tag: [],
        facility: [],
        usn: [],
        meter: [],
        subMeter: [],
        timeline: {
            type: 'is',
            value: 'previous2Months',
        },
    }

export const DEFAULT_BILLING_FILTERING: ReportFiltersValues = {
    commodity: [],
    contract: [],
    timeline: {
        type: 'is',
        value: 'last12Months',
    },
    account: [],
    node: [],
    facility: [],
    tag: [],
    provider: [],
    usn: [],
    invoiceNo: [],
    meter: [],
    calculation: 'productionMonth',
    unitOfMeasureId: [],
}

export const DEFAULT_USAGE_AND_COST_DATA_SETS: Array<UsageCostDataSet> = [
    'date',
    'cost_totalInvoiced',
    'usage_commodityUsage',
]

export const DEFAULT_BENCHMARKING_REPORT_SETTINGS: BenchmarkingReportSettings =
    {
        display: 'facility',
        basis: 'usage',
        analysisType: 'trend',
        comparisonPeriod: 'previousYear',
        applySquareFootage: false,
        applyWeatherNormalization: false,
    }

export const DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS: TotalEmissionsReportSettings =
    {
        type: 'emissionTypes',
        scope: 'all',
        scope2type: 'marketBased',
    }

export const DEFAULT_GHG_EMISSIONS_REPORT_SETTINGS: GHGEmissionsReportSettings =
    {
        scope: 'scope2',
        scope2type: 'all',
        showTotals: true,
    }

export const DEFAULT_INTERVAL_REPORT_TIMEZONE_ENTRY: TimezoneEntry = {
    timezone: 'Central Standard Time',
    timezoneId: 0,
    utcOffset: 'UTC-06:00',
}

export const DEFAULT_INTERVAL_REPORT_SETTINGS: IntervalReportSettings = {
    dataGrouping: 'aggregate',
    dataType: 'demandAverage',
    display: 'meters',
    timeInterval: 'oneHour',
    timezone: 'Central Standard Time',
    type: 'utilityData',
    showHighlights: true,
    granularity: '2_months',
}

export const INTERVAL_MAX_METERS_DISPLAY_COUNT = 20

export const DEFAULT_ELECTRICITY_MIX_REPORT_SETTINGS: TotalEmissionsReportSettings =
    {
        type: 'electricitySources',
        scope: 'scope2',
        scope2type: 'marketBased',
    }

export const ALERT_DESCRIPTION_MAP: Record<TotalEmissionsReportType, string> = {
    commodities: 'sustainability.report_settings.alert',
    electricitySources: 'sustainability.report_settings.alert_electricity',
    emissionTypes: 'sustainability.report_settings.alert_emissions',
}

export const SQUARE_FOOTAGE_UNIT = 'ft²'

export const iconMap: Record<number, string> = {
    1: 'electric_bolt', // Electricity
    2: 'local_fire_department', // Natural gas
    3: 'water_drop', // Water
    4: 'delete', // Waste
    5: 'valve', // Sewer
    6: 'filter_drama', // CO2
    7: 'oil_barrel', // Methane
    8: 'recycling', // Recycling
    10: 'propane_tank', // Propane
    11: 'mop', // Cleaning
    12: 'waves', // Irrigation
    13: 'rainy', // Stormwater
    14: 'ac_unit', // Snow and Ice
    15: 'pest_control', // Pest Management
    16: 'vpn_key', // Rent
    17: 'mail_outline', // Postage
    18: 'grass', // Lawn Care
    19: 'local_police', // Security
    20: 'print', // Office Supplies
    21: 'workspaces', // Miscellaneous,
    27: 'heat', // Steam
    28: 'propane', // Fuel Gas
    29: 'propane', // Nitrogen
    30: 'podcasts', // Telecom
    31: 'recycling', // Recycled waster
    32: 'water_pump', // Wastewater
    33: 'local_drink', // Recycled Wastewater
    34: 'heat_pump', // Chilled Water
}
